import { DialogContent, InputAdornment, TextField } from '@mui/material';
import { useMutation } from '@apollo/client';
import { useParams } from 'react-router-dom';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';

import { AvatarImage } from '../Avatar';
import { SHARE_PROJECT } from '../../apis/project';
import Button from '../atoms/button';
import Toaster from '../Snackbar/Toaster';
import useLoader from '../../context/LoaderContext';
import useWorkspace from '../../context/WorkspaceContext';

const propTypes = {
  openToggle: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  sharedProjectData: PropTypes.array.isRequired,
  profiles: PropTypes.array.isRequired,
  project: PropTypes.oneOfType([PropTypes.object]),
};

const modalTitle = 'Share Project';
const closeButton = 'Done';

const ModalAddProject = ({ openToggle, handleClose, onDone, sharedProjectData, profiles, project }) => {
  // init
  const { workspaceProfile } = useWorkspace();
  const { setIsLoading } = useLoader();
  const { id: projectId } = useParams();

  // states
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [toast, setToast] = useState({ open: false, message: '', autoHideDuration: 3000 });

  // mutations/queries
  const [shareProject] = useMutation(SHARE_PROJECT);

  // Effect to filter profiles based on sharedProjectData
  useEffect(() => {
    if (profiles.length > 0 && sharedProjectData && project) {
      const sharedUsers = sharedProjectData.map((profileData) => profileData.workspaceProfileId);
      const sharedUsersSet = new Set(sharedUsers.map(String));

      const filteredData = profiles.filter(
        (profileData) => !sharedUsersSet.has(String(profileData.id)) && profileData.id !== workspaceProfile.id && profileData.id !== parseInt(project.createdBy, 10),
      );
      setFilteredProfiles(filteredData);
    }
  }, [profiles, sharedProjectData, project]);

  const ShareProject = async () => {
    if (selectedOptions.length === 0) {
      handleClose();
      return;
    }
    setIsLoading(true);
    try {
      const workspaceProfileIds = selectedOptions.map((opt) => opt.id);
      handleClose();
      const variables = {
        input: { workspaceProfileIds, projectId: parseInt(projectId, 10) },
      };
      const { data } = await shareProject({
        variables,
      });
      if (data) {
        setIsLoading(false);
      }
      setToast((prevstate) => ({ ...prevstate, message: 'Project Shared successfully', open: true, type: 'success' }));
      onDone();
    } catch (error) {
      setIsLoading(false);
      setToast((prevstate) => ({ ...prevstate, message: 'Error in sharing project', open: true, type: 'error' }));
    } finally {
      setSelectedOptions([]);
      setIsLoading(false);
    }
  };

  const filterOption = (options, state) => {
    const newOptions = [];
    options.forEach((element) => {
      if (state !== '') {
        if (
          element.displayName.includes(state.toLowerCase()) ||
          element.userWorkspace.email.replace(',', '').toLowerCase().includes(state.toLowerCase())
        ) {
          newOptions.push(element);
        }
      } else {
        newOptions.push(element);
      }
    });
    return newOptions;
  };

  const selectUser = (option) => {
    setSelectedOptions([...selectedOptions, option]);
    setFilteredProfiles(filteredProfiles.filter((profile) => profile.id !== option.id));
  };

  const unSelectUser = (option) => {
    setSelectedOptions(selectedOptions.filter((opt) => opt.id !== option.id));
    setFilteredProfiles([...filteredProfiles, option]);
  };

  const handleToastClose = () => {
    setToast((prevstate) => ({ ...prevstate, open: false }));
  };

  const chipLabel = (option, index) => (
    <Chip
      onDelete={() => unSelectUser(option)}
      className="user-chip"
      size="small"
      avatar={<AvatarImage borderGray name={option.displayName} alt={option.displayName} fontSize={15} height={30} width={30} initials={2} src={option.logo} />}
      key={option.displayName + index}
      label={`${option.displayName}`}
    />
  );

  const handleCloseClicked = () => {
    handleClose();
    setSelectedOptions([]);
  };

  return (
    <>
      <Toaster anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} onClose={handleToastClose} {...toast} />
      <Dialog
        className="custom-modal-dialog share-project-dialog"
        fullWidth
        open={openToggle}
        onClose={handleClose}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle id="responsive-dialog-title">{modalTitle}</DialogTitle>
        <DialogContent className="dialog-content">
          <TextField
            className="search-input share-project-search-input"
            placeholder="Add people to project"
            onChange={(event) => setFilteredProfiles(filterOption(profiles, event.target.value))}
            multiline
            InputProps={{
              startAdornment: (
                <>
                  <InputAdornment style={{ color: 'rgba(0, 0, 0, 0.4)', padding: '10px 0px 10px 10px', flex: 1, minHeight: 40 }} position="start">
                    <SearchIcon className="search-icon" />
                  </InputAdornment>
                  {!!selectedOptions.length && <div style={{ width: '100%', padding: '5px 16px' }} className="chips">{selectedOptions.map((option, index) => chipLabel(option, index))}</div>}
                </>
              ),
            }}
          />
          {filteredProfiles.length > 0
            ? filteredProfiles.map((profile) => (
              <CardHeader
                className="user-list-header"
                onClick={() => selectUser(profile)}
                key={profile.userWorkspace.email + profile.displayName}
                style={{ padding: 0, width: '100%' }}
                avatar={<AvatarImage borderGray name={profile.displayName} alt={profile.displayName} fontSize={15} height={30} width={30} initials={2} src={profile.logo} />}
                action={<IconButton aria-label="settings" />}
                title={`${profile.displayName}`}
                subheader={profile.userWorkspace.email}
              />
            ))
            : <div className="no-search-user">No Users</div>}
        </DialogContent>
        <DialogActions className="dialog-actions">
          <Button primary outlined onClick={handleCloseClicked}>Cancel</Button>
          <Button primary onClick={ShareProject}>{closeButton}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
ModalAddProject.propTypes = propTypes;
export default ModalAddProject;
